import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Select,
  useToast,
  HStack,
  Icon
} from "@chakra-ui/react";
import { FaBuilding } from "react-icons/fa";
import client from "../../../../../helpers/Client";
import urlToId from "../../../../../helpers/UrlToId";
import MapBoxField from "../../../../../components/map/MapBoxField";

const AssignUnitModal = ({
  ticket,
  state,
  revalidator,
}) => {
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [subRegions, setSubRegions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedSubRegion, setSelectedSubRegion] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");

  // Map-based new unit creation
  const [createMode, setCreateMode] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [projectKind, setProjectKind] = useState("collective");
  const [aptNum, setAptNum] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newCoords, setNewCoords] = useState(null);
  const [mapProvinceName, setMapProvinceName] = useState("");
  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState("");
  const [districtsForCity, setDistrictsForCity] = useState([]);
  const [districtId, setDistrictId] = useState("");
  const toast = useToast();

  // Load countries on modal open
  useEffect(() => {
    if (state.isOpen) {
      client.get('/public/countries/?has_project=true').then((resp) => {
        const items = Array.isArray(resp.data?.results) ? resp.data.results : resp.data;
        setCountries(items || []);
      }).catch((error) => {
        console.error('Error loading countries:', error);
      });
    }
  }, [state.isOpen]);

  // Load regions when country changes
  useEffect(() => {
    if (!selectedCountry) return;

    client.get(`/public/cities/?country=${urlToId(selectedCountry)}`)
      .then((resp) => {
        const items = Array.isArray(resp.data?.results) ? resp.data.results : resp.data;
        setRegions(items || []);
      })
      .catch((error) => console.error('Error loading regions:', error));
  }, [selectedCountry]);

  // Load sub-regions when region changes
  useEffect(() => {
    if (!selectedRegion) return;

    client.get(`/public/districts/?city=${urlToId(selectedRegion)}`)
      .then((resp) => {
        const items = Array.isArray(resp.data?.results) ? resp.data.results : resp.data;
        setSubRegions(items || []);
      })
      .catch((error) => console.error('Error loading sub-regions:', error));
  }, [selectedRegion]);

  // Load projects when sub-region changes
  useEffect(() => {
    if (!selectedSubRegion) return;

    client.get(`/projects/?district=${urlToId(selectedSubRegion)}`)
      .then((resp) => {
        const items = Array.isArray(resp.data?.results) ? resp.data.results : resp.data;
        setProjects(items || []);
      })
      .catch((error) => console.error('Error loading projects:', error));
  }, [selectedSubRegion]);

  // Load units when project changes
  useEffect(() => {
    if (!selectedProject) return;

    client.get(`/units/?project=${urlToId(selectedProject)}`)
      .then((resp) => {
        const items = Array.isArray(resp.data?.results) ? resp.data.results : resp.data;
        setUnits(items || []);
      })
      .catch((error) => console.error('Error loading units:', error));
  }, [selectedProject]);

  // Load cities when modal opens (for TKGM mapping)
  useEffect(() => {
    if (!state.isOpen) return;
    client.get('/public/cities/').then((resp) => {
      const items = Array.isArray(resp.data?.results) ? resp.data.results : resp.data;
      setCities(items || []);
    }).catch(() => { });
  }, [state.isOpen]);

  // Resolve city by province name, then load districts
  useEffect(() => {
    if (!mapProvinceName || !cities.length) return;
    const match = cities.find((c) => String(c.name).toLowerCase() === String(mapProvinceName).toLowerCase());
    if (match) {
      const cid = urlToId(match.url);
      setCityId(cid);
      client.get(`/public/districts/?city=${cid}`).then((resp) => {
        const items = Array.isArray(resp.data?.results) ? resp.data.results : resp.data;
        setDistrictsForCity(items || []);
      }).catch(() => setDistrictsForCity([]));
    } else {
      setCityId("");
      setDistrictsForCity([]);
    }
  }, [mapProvinceName, cities]);

  const resolveDistrictIdByName = (name) => {
    if (!name) return "";
    const d = (districtsForCity || []).find((x) => String(x.name).toLowerCase() === String(name).toLowerCase());
    return d ? urlToId(d.url) : "";
  };

  const handleSubmit = async () => {
    if (createMode) {
      if (!projectName || !newAddress || !newCoords || !districtId) {
        toast({ title: "Eksik bilgi", description: "Proje adı, adres, ilçe ve konum seçimi gerekli.", status: "error" });
        return;
      }
      const apt = Number(String(aptNum).replace(/\D+/g, "")) || 1;
      setLoading(true);
      try {
        const payload = {
          project_name: projectName,
          kind: projectKind,
          address: newAddress,
          district_id: districtId,
          latitude: newCoords.lat,
          longitude: newCoords.lng,
          apt_num: apt
        };
        const { data } = await client.post('/units/create_from_address/', payload);
        const unitURL = data?.unit_url || data?.unit || "";
        if (!unitURL) throw new Error("Ünite oluşturma yanıtı geçersiz");
        await client.patch(`/tickets/${urlToId(ticket.url)}/`, { unit: unitURL });
        toast({ title: "Ünite oluşturuldu ve atandı", status: "success" });
        revalidator.revalidate();
        handleClose();
      } catch (error) {
        toast({ title: "Hata", description: error.response?.data?.detail || error.message || "İşlem başarısız", status: "error" });
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!selectedUnit) {
      toast({
        title: "Hata",
        description: "Lütfen bir ünite seçin.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      await client.patch(`/tickets/${urlToId(ticket.url)}/`, {
        unit: selectedUnit
      });

      toast({
        title: "Başarılı",
        description: "Ünite başarıyla atandı.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      revalidator.revalidate();
      state.onClose();
    } catch (error) {
      console.error('Error assigning unit:', error);
      toast({
        title: "Hata",
        description: error.response?.data?.detail || "Ünite ataması yapılamadı.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset all selections when modal closes
    setSelectedCountry("");
    setSelectedRegion("");
    setSelectedSubRegion("");
    setSelectedProject("");
    setSelectedUnit("");
    setRegions([]);
    setSubRegions([]);
    setProjects([]);
    setUnits([]);
    setCreateMode(false);
    setProjectName("");
    setProjectKind("collective");
    setAptNum("");
    setNewAddress("");
    setNewCoords(null);
    setMapProvinceName("");
    setCities([]);
    setCityId("");
    setDistrictsForCity([]);
    setDistrictId("");
    state.onClose();
  };

  return (
    <Modal isOpen={state.isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={FaBuilding} color="blue.500" />
            <Text>Ünite Ata</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Text fontWeight="semibold">{createMode ? "Yeni Ünite Oluştur ve Ata" : "Mevcut Ünite Ata"}</Text>
              <Button size="sm" variant="outline" onClick={() => setCreateMode(!createMode)}>
                {createMode ? "Mevcut Üniteye Dön" : "Yeni Ünite Oluştur"}
              </Button>
            </HStack>
            {!createMode && (
              <div>
                <Text mb={2} fontWeight="semibold">Ülke</Text>
                <Select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  placeholder="Ülke seçin..."
                  isDisabled={countries.length === 0}
                >
                  {countries.map((country) => (
                    <option key={country.url} value={country.url}>
                      {country.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {!createMode && (
              <div>
                <Text mb={2} fontWeight="semibold">İl</Text>
                <Select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  placeholder="İl seçin..."
                  isDisabled={regions.length === 0}
                >
                  {regions.map((region) => (
                    <option key={region.url} value={region.url}>
                      {region.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {!createMode && (
              <div>
                <Text mb={2} fontWeight="semibold">İlçe</Text>
                <Select
                  value={selectedSubRegion}
                  onChange={(e) => setSelectedSubRegion(e.target.value)}
                  placeholder="İlçe seçin..."
                  isDisabled={subRegions.length === 0}
                >
                  {subRegions.map((subRegion) => (
                    <option key={subRegion.url} value={subRegion.url}>
                      {subRegion.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {!createMode && (
              <div>
                <Text mb={2} fontWeight="semibold">Proje</Text>
                <Select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  placeholder="Proje seçin..."
                  isDisabled={projects.length === 0}
                >
                  {projects.map((project) => (
                    <option key={project.url} value={project.url}>
                      {project.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {!createMode && (
              <div>
                <Text mb={2} fontWeight="semibold">Ünite</Text>
                <Select
                  value={selectedUnit}
                  onChange={(e) => setSelectedUnit(e.target.value)}
                  placeholder="Ünite seçin..."
                  isDisabled={units.length === 0}
                >
                  {units.map((unit) => (
                    <option key={unit.url} value={unit.url}>
                      {unit.name || `Ünite ${unit.number}`}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {createMode && (
              <VStack spacing={3} align="stretch">
                <Text mb={2} fontWeight="semibold">Proje Tipi</Text>
                <Select value={projectKind} onChange={(e) => setProjectKind(e.target.value)} placeholder="Proje tipi">
                  <option value="collective">Toplu Konut</option>
                  <option value="villa">Villa</option>
                  <option value="office">Ofis</option>
                  <option value="other">Diğer</option>
                </Select>
                <Text mb={2} fontWeight="semibold">Proje Adı</Text>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Proje adı"
                  style={{ padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
                />
                <Text mb={2} fontWeight="semibold">Kapı No</Text>
                <input
                  type="text"
                  value={aptNum}
                  onChange={(e) => setAptNum(e.target.value)}
                  placeholder="Kapı No"
                  style={{ padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
                />
                <MapBoxField
                  onSelect={({ address, lat, lng, district }) => {
                    setNewAddress(address || "");
                    if (typeof lat === 'number' && typeof lng === 'number') setNewCoords({ lat, lng });
                    const did = resolveDistrictIdByName(district);
                    setDistrictId(did);
                  }}
                  onProvinceChange={(name) => setMapProvinceName(name)}
                />
                {newCoords && (
                  <HStack>
                    <Text>Lat: {Number(newCoords.lat).toFixed(6)}</Text>
                    <Text>Lng: {Number(newCoords.lng).toFixed(6)}</Text>
                  </HStack>
                )}
                <Select
                  value={districtId}
                  onChange={(e) => setDistrictId(e.target.value)}
                  placeholder="İlçe (otomatik seçilmediyse buradan seçin)"
                >
                  {districtsForCity.map((d) => (
                    <option key={d.url} value={urlToId(d.url)}>{d.name}</option>
                  ))}
                </Select>
              </VStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={handleClose}>
            Vazgeç
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText={createMode ? "Oluşturuluyor..." : "Atanıyor..."}
          >
            {createMode ? "Oluştur ve Ata" : "Ünite Ata"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AssignUnitModal;
